import { Target, Eye, Compass, CheckCircle2 } from "lucide-react";
import Seo from "../components/Seo";
import { PageHero, Reveal, SectionHead } from "../components/common/Section";
import { TRUST_PILLARS, APPROACH } from "../data/content";
import { Link } from "react-router-dom";

const IMG = "https://static.prod-images.emergentagent.com/jobs/b4dad798-223e-4abe-ad56-1c6e877c5f2b/images/faf44793e5804afc8c1b343a72b6cc6e19df0584586b57fce1cad72545289efa.jpeg";

const MV = [
  { icon: Target, title: "Our Mission", text: "To make high-quality financial thinking accessible, helping every individual and business plan, protect and grow wealth with confidence and clarity." },
  { icon: Eye, title: "Our Vision", text: "To become India's most trusted financial intelligence platform, where technology, research and human guidance work together for long-term financial wellbeing." },
  { icon: Compass, title: "Our Approach", text: "Education first, needs before products, transparency always. We combine analytics with personal conversations to build plans you understand." },
];

const WHY = [
  "Research and analytics-driven frameworks, not sales scripts",
  "Clear distinction between education, guidance and regulated services",
  "No fabricated track records, ratings or guaranteed-return promises",
  "Interactive tools and a client portal to track goals and reports",
  "Plans that cover protection, investing, tax awareness and retirement together",
  "Long-term relationships with periodic reviews",
];

export default function About() {
  return (
    <>
      <Seo title="About Us" path="/about" description="Quantora Analytics Private Limited is a financial analytics and solutions company helping people make informed financial decisions." />
      <PageHero eyebrow="About Quantora" title="Financial Intelligence Meets Your Future." text="Quantora is a financial analytics and solutions company focused on helping people make informed financial decisions through technology, research, education and personalized financial guidance." />
      <section className="py-24">
        <div className="container-q grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <SectionHead eyebrow="Company introduction" title="Built for people who want to understand their money." />
            <div className="mt-6 space-y-4 text-slate-600">
              <p>Quantora Analytics Private Limited was founded on a simple belief: good financial decisions start with good information. Too many people buy financial products without understanding them, or postpone planning because it feels complicated.</p>
              <p>We bring together financial analytics, research and human guidance across insurance, mutual funds, investment planning, wealth and tax planning, and financial education, so individuals and businesses can make smarter, better-informed choices.</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}><img src={IMG} alt="Quantora office" className="aspect-[4/3] w-full rounded-3xl object-cover shadow-2xl" loading="lazy" /></Reveal>
        </div>
      </section>
      <section className="bg-slate-100/70 py-24">
        <div className="container-q grid gap-6 md:grid-cols-3">
          {MV.map((m, i) => (
            <Reveal key={m.title} delay={i * 0.08} className="rounded-3xl border border-slate-200 bg-white p-8 transition-[transform,box-shadow] hover:-translate-y-1 hover:shadow-xl">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-navy text-gold"><m.icon size={22} /></div>
              <h3 className="mt-6 text-xl font-bold text-navy" data-testid={`about-${m.title.toLowerCase().replace(/\s/g, "-")}`}>{m.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{m.text}</p>
            </Reveal>
          ))}
        </div>
      </section>
      <section className="py-24">
        <div className="container-q grid gap-14 lg:grid-cols-2">
          <div>
            <SectionHead eyebrow="Why choose Quantora" title="What makes us different." />
            <ul className="mt-8 space-y-4">
              {WHY.map((w) => <li key={w} className="flex gap-3 text-slate-700"><CheckCircle2 size={20} className="mt-0.5 shrink-0 text-mint" />{w}</li>)}
            </ul>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {APPROACH.map((s) => (
              <div key={s.step} className="rounded-2xl bg-navy p-6 text-white">
                <span className="font-num text-sm text-gold">{s.step}</span>
                <h4 className="mt-3 text-lg font-semibold">{s.title}</h4>
                <p className="mt-2 text-sm text-slate-400">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-slate-100/70 py-20">
        <div className="container-q grid gap-6 md:grid-cols-4">
          {TRUST_PILLARS.map((p) => <div key={p.title} className="flex items-center gap-3 text-sm font-semibold text-navy"><p.icon className="text-gold" size={20} />{p.title}</div>)}
        </div>
        <div className="container-q mt-12">
          <Link to="/book-consultation" data-testid="about-book-btn" className="inline-flex rounded-full bg-navy px-7 py-3.5 text-sm font-semibold text-white hover:bg-navy-700">Book a Free Consultation</Link>
        </div>
      </section>
    </>
  );
}
